/**
 * <shopify-stone-filters> — Storefront-API-driven stone collection page.
 *
 * Mirrors the legacy <stone-filters> UX (sidebar facets on desktop, mobile drawer,
 * grid/table toggle, sortable table columns, 4 sort options) but for Shopify-product
 * stones (custom.stone_* metafields). Replaces <collection-filters> on stone
 * collections such as /collections/moissanites-1.
 *
 * Attrs:
 *   stone-type          — 'moissanite' | 'gemstone' | 'diamond'  (label only)
 *   collection-handle   — Shopify collection handle (required)
 *   default-sort        — optional, defaults to 'price-asc'
 *
 * Reuses globals: window.theme.shopify.executeQuery, window.theme.money.format
 */

const PAGE_SIZE = 60;
const VIEW_MODE_KEY = 'shopify-stone-view-mode';

const SORT_OPTIONS = [
  { id: 'price-asc',  label: 'Price: Low to High',  sortKey: 'PRICE',  reverse: false, sortField: 'total_price' },
  { id: 'price-desc', label: 'Price: High to Low',  sortKey: 'PRICE',  reverse: true,  sortField: 'total_price' },
  { id: 'carat-asc',  label: 'Carat: Low to High',  sortKey: null,     reverse: false, sortField: 'carat' },
  { id: 'carat-desc', label: 'Carat: High to Low',  sortKey: null,     reverse: true,  sortField: 'carat' },
];

const TABLE_COLUMNS = [
  { key: 'shape',  label: 'Shape',  sortable: false, sortField: null },
  { key: 'carat',  label: 'Carat',  sortable: true,  sortField: 'carat' },
  { key: 'price',  label: 'Price',  sortable: true,  sortField: 'total_price' },
  { key: 'cut',    label: 'Cut',    sortable: false, sortField: null },
  { key: 'origin', label: 'Origin', sortable: false, sortField: null },
];

// Gemstones have no cut; show Grade + Origin (matching the gemstone cards).
const GEMSTONE_TABLE_COLUMNS = [
  { key: 'shape',  label: 'Shape',  sortable: false, sortField: null },
  { key: 'carat',  label: 'Carat',  sortable: true,  sortField: 'carat' },
  { key: 'price',  label: 'Price',  sortable: true,  sortField: 'total_price' },
  { key: 'grade',  label: 'Grade',  sortable: false, sortField: null },
  { key: 'origin', label: 'Origin', sortable: false, sortField: null },
];

const QUERY = `
  query stoneCollection(
    $handle: String!
    $first: Int!
    $after: String
    $sortKey: ProductCollectionSortKeys = PRICE
    $reverse: Boolean = false
    $filters: [ProductFilter!]
  ) {
    collection(handle: $handle) {
      products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse, filters: $filters) {
        edges {
          node {
            id
            handle
            title
            featuredImage { url altText }
            priceRange { minVariantPrice { amount currencyCode } }
            compareAtPriceRange { minVariantPrice { amount currencyCode } }
            stone_shape:   metafield(namespace: "custom", key: "stone_shape")        { value }
            stone_carat:   metafield(namespace: "custom", key: "stone_carat_weight") { value }
            stone_cut:     metafield(namespace: "custom", key: "stone_cut")          { value }
            stone_color:   metafield(namespace: "custom", key: "stone_color")        { value }
            stone_clarity: metafield(namespace: "custom", key: "stone_clarity")      { value }
            stone_grade:   metafield(namespace: "custom", key: "stone_grade")        { value }
            stone_origin:  metafield(namespace: "custom", key: "origin")             { value }
          }
        }
        filters {
          id
          label
          type
          values { id label count input }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`;

const CHEVRON_SVG = `
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M5.29289 7.29289C5.68342 6.90237 6.31658 6.90237 6.70711 7.29289L10 10.5858L13.2929 7.29289C13.6834 6.90237 14.3166 6.90237 14.7071 7.29289C15.0976 7.68342 15.0976 8.31658 14.7071 8.70711L10.7071 12.7071C10.3166 13.0976 9.68342 13.0976 9.29289 12.7071L5.29289 8.70711C4.90237 8.31658 4.90237 7.68342 5.29289 7.29289Z" fill="currentColor"/>
  </svg>
`;

const FILTER_ICON_SVG = `
  <svg width="100%" height="100%" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.6667 11.1663C10.2065 11.1663 9.83374 11.5401 9.83374 12.0003C9.83392 12.4604 10.2066 12.8333 10.6667 12.8333C11.1269 12.8333 11.4996 12.4604 11.4998 12.0003C11.4998 11.5401 11.127 11.1663 10.6667 11.1663ZM5.33374 7.16632C4.8735 7.16632 4.49976 7.54007 4.49976 8.00031C4.49993 8.46039 4.87361 8.83331 5.33374 8.83331C5.79372 8.83314 6.16657 8.46028 6.16675 8.00031C6.16675 7.54018 5.79383 7.1665 5.33374 7.16632ZM10.6667 3.16632C10.2065 3.16632 9.83374 3.54007 9.83374 4.0003C9.83392 4.46039 10.2066 4.83331 10.6667 4.83331C11.1269 4.83331 11.4996 4.46039 11.4998 4.0003C11.4998 3.54007 11.127 3.16632 10.6667 3.16632ZM10.6667 10.1663C11.5059 10.1663 12.2118 10.7309 12.4294 11.5003L13.3337 11.5003C13.6097 11.5005 13.8337 11.7243 13.8337 12.0003C13.8336 12.2762 13.6096 12.5001 13.3337 12.5003L12.4285 12.5003C12.2105 13.2693 11.5056 13.8333 10.6667 13.8333C9.82783 13.8333 9.12194 13.2694 8.90405 12.5003L2.66675 12.5003C2.39071 12.5003 2.16692 12.2763 2.16675 12.0003C2.16675 11.7242 2.39061 11.5003 2.66675 11.5003L8.90308 11.5003C9.12068 10.7307 9.82752 10.1663 10.6667 10.1663ZM10.6667 2.16632C11.5059 2.16632 12.2118 2.73087 12.4294 3.5003L13.3337 3.5003C13.6097 3.50048 13.8337 3.72427 13.8337 4.0003C13.8336 4.27619 13.6096 4.50013 13.3337 4.5003L12.4285 4.5003C12.2105 5.2693 11.5056 5.83331 10.6667 5.83331C9.82783 5.83331 9.12194 5.26943 8.90405 4.5003L2.66675 4.50031C2.39071 4.50031 2.16692 4.2763 2.16675 4.00031C2.16675 3.72416 2.39061 3.50031 2.66675 3.50031L8.90308 3.5003C9.12068 2.73074 9.82752 2.16632 10.6667 2.16632ZM5.33374 6.16632C6.17277 6.16647 6.87881 6.73092 7.09644 7.5003L13.3337 7.5003C13.6097 7.50048 13.8337 7.72427 13.8337 8.0003C13.8336 8.27619 13.6096 8.50013 13.3337 8.5003L7.09546 8.5003C6.87755 9.26925 6.17246 9.83317 5.33374 9.83331C4.49476 9.83331 3.7879 9.26952 3.57007 8.50031L2.66675 8.50031C2.39071 8.50031 2.16692 8.2763 2.16675 8.00031C2.16675 7.72416 2.39061 7.50031 2.66675 7.50031L3.57007 7.50031C3.7877 6.73078 4.49454 6.16632 5.33374 6.16632Z" fill="currentColor"/>
  </svg>
`;

if (!customElements.get('shopify-stone-filters')) {

  class ShopifyStoneFilters extends HTMLElement {

    constructor() {
      super();
      this.allItems = [];
      this.filterDefs = [];
      this.activeFilters = new Map(); // key -> {input, label, filterId}
      this.viewMode = sessionStorage.getItem(VIEW_MODE_KEY) || 'grid';
      this.sortId = this.getAttribute('default-sort') || 'price-asc';
      this.priceRange = { min: '', max: '' };
      this.caratRange = { min: '', max: '' };
      this.caratFacetValues = null;
      this.caratFullBounds = null;
      this.loaded = false;
    }

    get stoneType() { return (this.getAttribute('stone-type') || 'stone').toLowerCase(); }
    get collectionHandle() { return this.getAttribute('collection-handle') || ''; }

    async connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      this.injectLayoutStyles();
      this.readUrlState();
      this.renderShell();
      this.bindShellEvents();
      this.renderSkeleton();
      this.renderFacetsSkeleton();
      await this.waitForApi();
      await this.fetchAll();
      this.reconcileNativeFilters();
      // A carat range from the URL can't be applied on the first fetch (the
      // carat facet's value list isn't known yet) — refetch now that it is.
      if ((this.caratRange.min || this.caratRange.max) && this.caratFacetValues?.length) {
        await this.fetchAll();
      }
      this.renderFacets();
      this.applyAndRender();
    }

    // Mobile toolbar gaps via injected CSS — gap-y-* Tailwind classes used in this
    // JS string aren't scanned by the build, so they don't compile.
    injectLayoutStyles() {
      if (document.getElementById('stone-filters-layout-styles')) return;
      const style = document.createElement('style');
      style.id = 'stone-filters-layout-styles';
      style.textContent = `
        @media (max-width: 1023px){
          shopify-stone-filters [data-toolbar]{ row-gap: 24px; }
          shopify-stone-filters [data-active-facets]{ display:flex; flex-wrap:wrap; row-gap:8px; column-gap:8px; }
        }
      `;
      document.head.appendChild(style);
    }

    /* ---------------- loading states ---------------- */

    // Full-page Allurez-logo loader (theme-loading.js) while filters/sort refetch.
    showLoading() {
      if (window.theme?.ThemeLoader?.enable) window.theme.ThemeLoader.enable();
    }

    hideLoading() {
      if (window.theme?.ThemeLoader?.disable) window.theme.ThemeLoader.disable();
    }

    // Grey skeleton cards shown on initial load before the first fetch resolves.
    // Styles are injected (not Tailwind classes) since arbitrary values in a JS
    // string aren't compiled by the Tailwind build.
    injectSkeletonStyles() {
      if (document.getElementById('stone-skeleton-styles')) return;
      const style = document.createElement('style');
      style.id = 'stone-skeleton-styles';
      style.textContent = `
        .stone-skeleton-card{ animation: stoneSkeletonPulse 1.4s ease-in-out infinite; }
        .stone-skeleton-card .sk{ background:#f0efef; border-radius:2px; }
        @keyframes stoneSkeletonPulse{ 0%,100%{opacity:1} 50%{opacity:.45} }
      `;
      document.head.appendChild(style);
    }

    // Grey skeleton for the desktop sidebar facets before the first fetch resolves.
    renderFacetsSkeleton(groups = 5) {
      const sidebar = this.querySelector('[data-sidebar-facets]');
      if (!sidebar) return;
      this.injectSkeletonStyles();
      const group = `
        <div class="flex flex-col gap-3 border-b border-base-border pb-4">
          <div class="sk" style="height:14px;width:45%;"></div>
          <div class="sk" style="height:10px;width:90%;"></div>
          <div class="sk" style="height:10px;width:80%;"></div>
          <div class="sk" style="height:10px;width:85%;"></div>
        </div>
      `;
      sidebar.innerHTML = `<div class="stone-skeleton-card flex flex-col gap-5 w-full">${group.repeat(groups)}</div>`;
    }

    renderSkeleton(count = 12) {
      const container = this.querySelector('[data-items-container]');
      if (!container) return;
      this.injectSkeletonStyles();
      container.className = 'grid grid-cols-2 gap-4 tablet:grid-cols-3 tablet:gap-4 laptop:grid-cols-3 laptop:gap-4';
      const card = `
        <div class="stone-skeleton-card">
          <div class="sk" style="aspect-ratio:1/1;"></div>
          <div class="sk" style="height:12px;margin-top:12px;width:75%;"></div>
          <div class="sk" style="height:12px;margin-top:8px;width:50%;"></div>
        </div>
      `;
      container.innerHTML = card.repeat(count);
    }

    waitForApi() {
      return new Promise((resolve) => {
        if (window.theme?.shopify?.executeQuery && window.theme?.money?.format) return resolve();
        const t = setInterval(() => {
          if (window.theme?.shopify?.executeQuery && window.theme?.money?.format) {
            clearInterval(t); resolve();
          }
        }, 50);
        setTimeout(() => { clearInterval(t); resolve(); }, 5000);
      });
    }

    /* ---------------- URL state ---------------- */

    readUrlState() {
      const params = new URLSearchParams(window.location.search);
      const sort = params.get('sort');
      if (sort && SORT_OPTIONS.find(o => o.id === sort)) this.sortId = sort;
      const view = params.get('view');
      if (view === 'grid' || view === 'table') this.viewMode = view;
      this.priceRange.min = params.get('price_min') || '';
      this.priceRange.max = params.get('price_max') || '';
      this.caratRange.min = params.get('carat_min') || '';
      this.caratRange.max = params.get('carat_max') || '';
      // Stash raw applied filter inputs as JSON in `f` param (preserves Shopify input shape)
      const f = params.get('f');
      if (f) {
        try {
          const parsed = JSON.parse(decodeURIComponent(f));
          if (Array.isArray(parsed)) {
            parsed.forEach(entry => this.activeFilters.set(entry.key, entry));
          }
        } catch (_) {}
      }
      // Also accept Shopify-native, human-writable filter params so a short URL
      // (e.g. ?filter.p.m.custom.stone_type=Blue+Sapphire) can be assigned to a
      // Shopify menu. Reconciled to canonical facet entries after the fetch.
      this.readNativeFilterParams(params);
    }

    // Parse Shopify's standard storefront filter params:
    //   filter.p.m.<namespace>.<key>=<value>   (product metafield)
    //   filter.p.tag=<value>                    (product tag)
    //   filter.v.price.gte / .lte               (price range)
    readNativeFilterParams(params) {
      params.forEach((value, rawKey) => {
        if (!rawKey.startsWith('filter.')) return;
        const val = String(value).trim();
        if (!val) return;

        if (rawKey === 'filter.v.price.gte') { this.priceRange.min = val; return; }
        if (rawKey === 'filter.v.price.lte') { this.priceRange.max = val; return; }

        let input = null;
        let filterId = rawKey;
        const mf = rawKey.match(/^filter\.p\.m\.([^.]+)\.(.+)$/);
        if (mf) {
          input = JSON.stringify({ productMetafield: { namespace: mf[1], key: mf[2], value: val } });
        } else if (rawKey === 'filter.p.tag') {
          input = JSON.stringify({ tag: val });
        }
        if (!input) return;

        // Provisional key; reconcileNativeFilters swaps it for `${def.id}::${v.id}`
        // once the API returns the real value id (so the facet shows checked).
        const key = `${filterId}::native:${val}`;
        this.activeFilters.set(key, { key, filterId, label: val, input, _native: true });
      });
    }

    // After fetchAll, replace provisional native entries with canonical facet
    // entries (matched by value label) so checkboxes/tiles render checked.
    reconcileNativeFilters() {
      Array.from(this.activeFilters.values())
        .filter(e => e && e._native)
        .forEach(entry => {
          const def = this.filterDefs.find(d => d.id === entry.filterId);
          const v = def && (def.values || []).find(
            val => String(val.label).toLowerCase() === String(entry.label).toLowerCase()
          );
          if (!v) return; // keep provisional entry; the filter still applies via input
          this.activeFilters.delete(entry.key);
          const key = `${def.id}::${v.id}`;
          this.activeFilters.set(key, { key, filterId: def.id, label: v.label, input: v.input });
        });
    }

    writeUrlState() {
      const params = new URLSearchParams();
      if (this.sortId !== 'price-asc') params.set('sort', this.sortId);
      if (this.viewMode !== 'grid') params.set('view', this.viewMode);
      if (this.priceRange.min) params.set('price_min', this.priceRange.min);
      if (this.priceRange.max) params.set('price_max', this.priceRange.max);
      if (this.caratRange.min) params.set('carat_min', this.caratRange.min);
      if (this.caratRange.max) params.set('carat_max', this.caratRange.max);
      if (this.activeFilters.size) {
        const arr = Array.from(this.activeFilters.values());
        params.set('f', encodeURIComponent(JSON.stringify(arr)));
      }
      const qs = params.toString();
      const url = window.location.pathname + (qs ? `?${qs}` : '');
      window.history.replaceState({}, '', url);
    }

    /* ---------------- API ---------------- */

    buildFilters() {
      const filters = [];
      this.activeFilters.forEach(entry => {
        try { filters.push(JSON.parse(entry.input)); } catch (_) {}
      });
      if (this.priceRange.min || this.priceRange.max) {
        const price = {};
        if (this.priceRange.min) price.min = parseFloat(this.priceRange.min);
        if (this.priceRange.max) price.max = parseFloat(this.priceRange.max);
        filters.push({ price });
      }
      // MetafieldFilter has no min/max on the Storefront API, so a carat range
      // is applied by OR-ing every discrete carat facet value inside it
      // (Shopify ORs values of the same metafield filter, ANDs across filters).
      if ((this.caratRange.min || this.caratRange.max) && this.caratFacetValues?.length) {
        const lo = this.caratRange.min !== '' ? parseFloat(this.caratRange.min) : -Infinity;
        const hi = this.caratRange.max !== '' ? parseFloat(this.caratRange.max) : Infinity;
        let matched = 0;
        this.caratFacetValues.forEach(v => {
          if (v.num < lo || v.num > hi) return;
          try { filters.push(JSON.parse(v.input)); matched++; } catch (_) {}
        });
        // Range excludes every carat value — match nothing instead of everything.
        if (!matched) {
          filters.push({ productMetafield: { namespace: 'custom', key: 'stone_carat_weight', value: '__none__' } });
        }
      }
      return filters;
    }

    async fetchAll() {
      const sort = SORT_OPTIONS.find(o => o.id === this.sortId) || SORT_OPTIONS[0];
      const sortKey = sort.sortKey || 'PRICE'; // server still uses some key; carat is re-sorted client-side
      const reverse = sort.sortKey ? sort.reverse : false;

      const filters = this.buildFilters();
      let after = null;
      let items = [];
      let filterDefs = [];
      this.truncated = false;

      try {
        while (true) {
          const data = await window.theme.shopify.executeQuery(QUERY, {
            handle: this.collectionHandle,
            first: PAGE_SIZE,
            after,
            sortKey,
            reverse,
            filters,
          });
          const conn = data?.collection?.products;
          if (!conn) break;
          items = items.concat(conn.edges.map(e => e.node));
          if (!filterDefs.length && conn.filters) filterDefs = conn.filters;
          if (!conn.pageInfo.hasNextPage) break;
          after = conn.pageInfo.endCursor;
          if (items.length >= 500) { this.truncated = true; break; } // safety cap — count is incomplete
        }
      } catch (err) {
        console.error('shopify-stone-filters fetch failed:', err);
      }

      this.filterDefs = filterDefs;
      this.allItems = items;
      this.captureCaratValues(filterDefs);
    }

    // Lock the carat facet's full value list (and bounds) on first sight.
    // Filtered responses shrink the list, and the slider needs stable bounds
    // plus the complete value set to translate a range into value filters.
    captureCaratValues(defs) {
      if (this.caratFacetValues?.length) return;
      const def = (defs || []).find(d => d.type === 'LIST' && this.isCaratFilter(d));
      if (!def?.values?.length) return;
      const values = def.values
        .map(v => ({ num: parseFloat(v.label), input: v.input }))
        .filter(v => !isNaN(v.num));
      if (!values.length) return;
      this.caratFacetValues = values;
      const nums = values.map(v => v.num);
      this.caratFullBounds = { lo: Math.min(...nums), hi: Math.max(...nums) };
    }

    /* ---------------- Shell render ---------------- */

    renderShell() {
      this.innerHTML = `
        <div class="stone-filters laptop:flex laptop:gap-8">
          <aside class="laptop:w-4/12 desktop:w-3/12">
            <form data-filters-form class="flex flex-col laptop:border-t laptop:border-base-border laptop:pt-5 laptop:pb-5">
              <side-drawer id="shopify-stone-filters-drawer">
                <div class="flex pb-4 px-4">
                  <div class="inline-flex items-center gap-2">
                    <span class="size-5">${FILTER_ICON_SVG}</span>
                    <span class="uppercase">Filters</span>
                  </div>
                </div>
                <div class="flex flex-col gap-4 h-[calc(100%-70px)]">
                  <div data-drawer-facets class="overflow-y-auto h-full px-4"></div>
                  <div class="flex justify-end shrink-0 px-4 pb-4">
                    <button data-drawer-close type="button" class="btn btn-secondary w-full">Show Results</button>
                  </div>
                </div>
              </side-drawer>

              <div data-sidebar-facets class="hidden w-full flex-col gap-5 laptop:flex"></div>

              <button type="button"
                class="btn btn-primary rounded-none fixed w-[200px] bottom-6 left-0 right-0 m-auto z-10 laptop:hidden"
                data-drawer-target="shopify-stone-filters-drawer"
                aria-label="Filters">
                <span class="size-5">${FILTER_ICON_SVG}</span>
                <span class="flex items-center gap-1">Stone Filters</span>
              </button>
            </form>
          </aside>

          <div class="laptop:w-8/12 desktop:w-9/12">
            <div data-toolbar class="flex flex-wrap w-full gap-y-6 pb-4 laptop:flex-nowrap laptop:justify-between laptop:gap-5 laptop:gap-y-0">
              <div data-active-facets class="flex flex-wrap items-center gap-2 w-full laptop:flex-1"></div>
              <div class="flex items-center justify-between w-full gap-4 laptop:w-auto laptop:flex-shrink-0 laptop:justify-end laptop:ml-auto">
                <div data-items-count class="hidden text-sm whitespace-nowrap laptop:block"></div>
                <select data-sort class="border border-base-border text-sm py-1 px-2 max-w-[220px] laptop:min-w-[200px] laptop:max-w-[260px]">
                  ${SORT_OPTIONS.map(o => `<option value="${o.id}" ${o.id === this.sortId ? 'selected' : ''}>${o.label}</option>`).join('')}
                </select>
                <div data-view-selector class="flex items-center">
                  <button type="button" data-view-mode="table" class="view-mode-btn p-2 transition-opacity" aria-label="Table view" title="Table view">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="${this.viewMode === 'table' ? '' : 'opacity-50'}">
                      <path d="M7 19H21V17H7V19ZM7 13H21V11H7V13ZM7 5V7H21V5H7Z" fill="#A7A5A6"/>
                      <path d="M5 19H3V17H5V19ZM5 13H3V11H5V13ZM5 5V7H3V5H5Z" fill="#A7A5A6"/>
                    </svg>
                  </button><button type="button" data-view-mode="grid" class="view-mode-btn p-2 transition-opacity" aria-label="Grid view" title="Grid view">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="${this.viewMode === 'grid' ? '' : 'opacity-50'}">
                      <path d="M7 19H5V17H7V19ZM7 13H5V11H7V13ZM7 5V7H5V5H7Z" fill="#231F20"/>
                      <path d="M13 19H11V17H13V19ZM13 13H11V11H13V13ZM13 5V7H11V5H13Z" fill="#231F20"/>
                      <path d="M19 19H17V17H19V19ZM19 13H17V11H19V13ZM19 5V7H17V5H19Z" fill="#231F20"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div data-items-container></div>
          </div>
        </div>
      `;
    }

    bindShellEvents() {
      // Sort
      const sortSelect = this.querySelector('[data-sort]');
      sortSelect.addEventListener('change', async () => {
        this.sortId = sortSelect.value;
        this.showLoading();
        this.writeUrlState();
        await this.fetchAll();
        this.renderFacets();
        this.applyAndRender();
        this.hideLoading();
      });

      // View toggle
      this.querySelectorAll('[data-view-mode]').forEach(btn => {
        btn.addEventListener('click', () => {
          const mode = btn.getAttribute('data-view-mode');
          if (mode === this.viewMode) return;
          this.viewMode = mode;
          sessionStorage.setItem(VIEW_MODE_KEY, mode);
          this.writeUrlState();
          this.querySelectorAll('[data-view-mode] svg').forEach(svg => {
            const parentMode = svg.closest('[data-view-mode]').getAttribute('data-view-mode');
            svg.classList.toggle('opacity-50', parentMode !== mode);
          });
          this.renderItems();
        });
      });

      // Drawer close button
      const drawer = this.querySelector('side-drawer#shopify-stone-filters-drawer');
      const closeBtn = this.querySelector('[data-drawer-close]');
      if (closeBtn && drawer) {
        closeBtn.addEventListener('click', () => {
          if (typeof drawer.close === 'function') drawer.close();
          else drawer.removeAttribute('open');
        });
      }
    }

    /* ---------------- Facets ---------------- */

    renderFacets() {
      const sidebar = this.querySelector('[data-sidebar-facets]');
      const drawer = this.querySelector('[data-drawer-facets]');
      if (!sidebar || !drawer) return;

      const html = this.buildFacetsHtml();
      sidebar.innerHTML = html;
      drawer.innerHTML = html;

      this.bindFacetEvents(sidebar);
      this.bindFacetEvents(drawer);
      this.renderActiveFacets();
    }

    buildFacetsHtml() {
      const blocks = [];

      // Stone (hardcoded — links to other stone collections, not really a filter)
      blocks.push(this.facetStoneHtml());

      // Native filters from API
      this.filterDefs.forEach(def => {
        if (def.type === 'PRICE_RANGE') {
          blocks.push(this.facetPriceHtml(def));
        } else if (this.isCaratFilter(def)) {
          blocks.push(this.facetCaratHtml(def));
        } else if (this.isShapeFilter(def)) {
          blocks.push(this.facetShapeHtml(def));
        } else if (this.isVarietyFilter(def)) {
          blocks.push(this.facetVarietyHtml(def));
        } else {
          blocks.push(this.facetListHtml(def));
        }
      });

      return blocks.join('');
    }

    isShapeFilter(def) {
      return /stone_shape/i.test(def.id) || /^shape$/i.test(def.label || '');
    }

    // The stone_type metafield facet holds gem varieties (Amethyst, Ruby, ...) on
    // gemstone collections. Render it as image tiles using the `gemstone` metaobject
    // swatches. Gated to gemstone collections so it stays a plain list elsewhere.
    isVarietyFilter(def) {
      if (this.stoneType !== 'gemstone') return false;
      return /stone_type/i.test(def.id) || /^gemstone$/i.test((def.label || '').trim());
    }

    isCaratFilter(def) {
      return /stone_carat_weight/i.test(def.id) || /carat/i.test(def.label || '');
    }

    descriptionFor(def) {
      if (this.isCaratFilter(def)) return this.getAttribute('carat-description') || '';
      return '';
    }

    facetShapeHtml(def) {
      return this.facetImageTileHtml(def, 'option-image-center-stone-shape-', this.getAttribute('shape-description') || '', 'Shape');
    }

    facetVarietyHtml(def) {
      return this.facetImageTileHtml(def, 'option-image-gemstone-', this.getAttribute('gemstone-description') || '', 'Gemstone');
    }

    facetImageTileHtml(def, imageClassPrefix, description, fallbackLabel) {
      const items = (def.values || []).map(v => {
        const key = `${def.id}::${v.id}`;
        const checked = this.activeFilters.has(key) ? 'checked' : '';
        const slug = theme.handleize(String(v.label || ''));
        return `
          <label class="shape-tile mb-0 cursor-pointer w-full hover:underline has-[input:checked]:bg-base-50 has-[input:checked]:font-medium has-[input:checked]:outline has-[input:checked]:outline-2 has-[input:checked]:outline-black has-[input:checked]:relative has-[input:checked]:z-10">
            <input
              type="checkbox"
              class="peer sr-only"
              data-facet-key="${this.escapeAttr(key)}"
              data-facet-id="${this.escapeAttr(def.id)}"
              data-facet-label="${this.escapeAttr(v.label)}"
              data-facet-input='${this.escapeAttr(v.input)}'
              ${checked}
            />
            <span class="flex flex-col items-center justify-center gap-1 peer-checked:font-medium" style="pointer-events:none;">
              <span class="size-6 bg-cover bg-center bg-no-repeat ${imageClassPrefix}${this.escapeAttr(slug)}"></span>
              <span class="line-clamp-3 text-xs text-center">${this.escapeHtml(v.label)}</span>
            </span>
          </label>
        `;
      }).join('');
      return `
        <details class="facet border-b border-base-border pb-4" data-facet-key="${this.escapeAttr(def.id)}" open>
          <summary class="flex items-center justify-between py-2 cursor-pointer select-none">
            <span class="uppercase text-sm">${this.escapeHtml(def.label || fallbackLabel)}</span>
            <span class="facet-toggle inline-flex items-center justify-center size-4 transition-transform">${CHEVRON_SVG}</span>
          </summary>
          ${description ? `<p class="text-sm text-primary-400 mt-2 mb-4">${this.escapeHtml(description)}</p>` : ''}
          <div class="option-tile mb-2" data-columns-count="4">
            <div class="option-tile_options grid grid-cols-4 relative border-l border-base-border [&>label]:border-r [&>label]:border-b [&>label]:border-base-border [&>label:nth-child(-n+4)]:border-t">
              ${items}
            </div>
          </div>
        </details>
      `;
    }

    facetStoneHtml() {
      const t = this.stoneType;
      const link = (href, label, active) => `
        <a href="${href}" class="${active ? 'active' : ''} mb-0 text-sm cursor-pointer w-full flex items-center justify-center text-center px-3 py-3 hover:underline">${label}</a>
      `;
      return `
        <details class="facet border-b border-base-border pb-4" data-facet-key="stone" open>
          <summary class="flex items-center justify-between py-2 cursor-pointer select-none">
            <span class="uppercase text-sm">Stone</span>
            <span class="facet-toggle inline-flex items-center justify-center size-4 transition-transform">${CHEVRON_SVG}</span>
          </summary>
          <div class="option-tile mb-2 mt-2" data-columns-count="2">
            <div class="option-tile_options grid grid-cols-2 relative border-l border-t border-base-border [&>a]:border-r [&>a]:border-b [&>a]:border-base-border">
              ${link('/collections/loose-diamonds', 'Diamond', t === 'diamond')}
              ${link('/collections/color-diamonds', 'Color Diamond', t === 'color-diamond')}
              ${link('/collections/gemstones', 'Gemstone', t === 'gemstone')}
              ${link('/collections/moissanites', 'Moissanite', t === 'moissanite')}
            </div>
          </div>
        </details>
      `;
    }

    facetCaratHtml(def) {
      this.captureCaratValues([def]);
      // Non-numeric carat labels — keep the plain checkbox list.
      if (!this.caratFullBounds) return this.facetListHtml(def);
      const lo = Math.floor(this.caratFullBounds.lo * 100) / 100;
      const hi = Math.ceil(this.caratFullBounds.hi * 100) / 100;
      const clamp = (v) => Math.min(Math.max(v, lo), hi);
      const curMin = this.caratRange.min !== '' ? clamp(Number(this.caratRange.min)) : lo;
      const curMax = this.caratRange.max !== '' ? clamp(Number(this.caratRange.max)) : hi;
      return this.rangeFacetHtml({
        key: 'carat',
        title: def.label || 'Carat',
        lo, hi, step: 0.01, curMin, curMax,
        prefix: '', suffix: 'ct',
        description: this.descriptionFor(def),
      });
    }

    facetPriceHtml(def) {
      const minDef = def.values?.[0]?.input ? JSON.parse(def.values[0].input) : null;
      const defLo = Math.floor(Number(minDef?.price?.min ?? 0));
      const defHi = Math.ceil(Number(minDef?.price?.max ?? 0)) || defLo;
      // Lock the full price bounds on first render. Re-deriving them from a
      // filtered response would shrink the track and make the handles jump.
      if (!this.priceFullBounds) this.priceFullBounds = { lo: defLo, hi: defHi };
      const lo = this.priceFullBounds.lo;
      const hi = this.priceFullBounds.hi;
      const clamp = (v) => Math.min(Math.max(v, lo), hi);
      const curMin = this.priceRange.min !== '' ? clamp(Number(this.priceRange.min)) : lo;
      const curMax = this.priceRange.max !== '' ? clamp(Number(this.priceRange.max)) : hi;
      return this.rangeFacetHtml({
        key: 'price',
        title: def.label,
        lo, hi, step: 1, curMin, curMax,
        prefix: '$', suffix: '',
        description: this.descriptionFor(def),
      });
    }

    // Shared dual-handle range facet (price, carat). `key` maps to the state
    // object (this.priceRange / this.caratRange) in bindRangeSliders.
    rangeFacetHtml({ key, title, lo, hi, step, curMin, curMax, prefix, suffix, description }) {
      this.injectPriceSliderStyles();
      const unitBefore = prefix ? `<span>${this.escapeHtml(prefix)}</span>` : '';
      const unitAfter = suffix ? `<span>${this.escapeHtml(suffix)}</span>` : '';
      const box = (which, value) => `
        <div class="flex items-center gap-1 border border-base-border px-3 py-2 w-1/2">
          ${unitBefore}
          <input type="text" inputmode="decimal" data-range-box="${which}" value="${value}" class="w-full min-w-0 border-0 p-0 outline-none bg-transparent text-sm" />
          ${unitAfter}
        </div>
      `;
      return `
        <details class="facet border-b border-base-border pb-4" data-facet-key="${key}" open>
          <summary class="flex items-center justify-between py-2 cursor-pointer select-none">
            <span class="uppercase text-sm">${this.escapeHtml(title)}</span>
            <span class="facet-toggle inline-flex items-center justify-center size-4 transition-transform">${CHEVRON_SVG}</span>
          </summary>
          ${description ? `<p class="text-sm text-primary-400 mt-2 mb-4">${this.escapeHtml(description)}</p>` : ''}
          <div class="stone-price-slider" data-range-key="${key}" data-lo="${lo}" data-hi="${hi}">
            <div class="stone-price-track"></div>
            <div data-range-fill class="stone-price-fill"></div>
            <input type="range" data-range-thumb="min" min="${lo}" max="${hi}" step="${step}" value="${curMin}" aria-label="Minimum ${key}" />
            <input type="range" data-range-thumb="max" min="${lo}" max="${hi}" step="${step}" value="${curMax}" aria-label="Maximum ${key}" />
          </div>
          <div class="flex items-center gap-3">
            ${box('min', curMin)}
            ${box('max', curMax)}
          </div>
        </details>
      `;
    }

    injectPriceSliderStyles() {
      if (document.getElementById('stone-price-slider-styles')) return;
      const style = document.createElement('style');
      style.id = 'stone-price-slider-styles';
      // Geometry lives here (not Tailwind) because arbitrary classes in a JS
      // string aren't scanned by the Tailwind build, so they never compile.
      style.textContent = `
        .stone-price-slider{ position:relative; height:18px; margin:18px 0 32px; }
        .stone-price-track{
          position:absolute; top:50%; transform:translateY(-50%); left:0; right:0;
          height:4px; background:#DBDBDB; border-radius:9999px;
        }
        .stone-price-fill{
          position:absolute; top:50%; transform:translateY(-50%);
          height:4px; background:#825C63; border-radius:9999px;
        }
        .stone-price-slider input[type="range"]{
          position:absolute; top:50%; transform:translateY(-50%); left:0;
          width:100%; height:18px; margin:0; padding:0; background:none; pointer-events:none;
          border:0 !important; outline:0; box-shadow:none;
          -webkit-appearance:none; appearance:none;
        }
        .stone-price-slider input[type="range"]:focus{ outline:0; }
        .stone-price-slider input[type="range"]::-webkit-slider-runnable-track{ background:transparent; border:none; }
        .stone-price-slider input[type="range"]::-moz-range-track{ background:transparent; border:none; }
        .stone-price-slider input[type="range"]::-webkit-slider-thumb{
          -webkit-appearance:none; appearance:none; width:18px; height:18px; border-radius:9999px;
          background:#825C63; border:none; cursor:pointer; pointer-events:auto;
        }
        .stone-price-slider input[type="range"]::-moz-range-thumb{
          width:18px; height:18px; border-radius:9999px; background:#825C63; border:none; cursor:pointer; pointer-events:auto;
        }
      `;
      document.head.appendChild(style);
    }

    // Wire every dual-handle range slider (price, carat) within a facet scope.
    bindRangeSliders(scope) {
      scope.querySelectorAll('[data-range-key]').forEach(wrap => {
        const range = wrap.dataset.rangeKey === 'carat' ? this.caratRange : this.priceRange;
        this.bindRangeSlider(wrap, range);
      });
    }

    bindRangeSlider(wrap, range) {
      const lo = Number(wrap.dataset.lo);
      const hi = Number(wrap.dataset.hi);
      const minThumb = wrap.querySelector('[data-range-thumb="min"]');
      const maxThumb = wrap.querySelector('[data-range-thumb="max"]');
      const fill = wrap.querySelector('[data-range-fill]');
      const facet = wrap.closest('details.facet');
      const minInput = facet?.querySelector('[data-range-box="min"]');
      const maxInput = facet?.querySelector('[data-range-box="max"]');
      if (!minThumb || !maxThumb) return;

      const span = (hi - lo) || 1;
      const pct = (v) => ((v - lo) / span) * 100;
      // Kill float noise from fractional steps (carat) without touching ints (price).
      const round = (v) => Math.round(v * 100) / 100;

      const paint = () => {
        const a = Math.min(Number(minThumb.value), Number(maxThumb.value));
        const b = Math.max(Number(minThumb.value), Number(maxThumb.value));
        fill.style.left = pct(a) + '%';
        fill.style.right = (100 - pct(b)) + '%';
        // Keep the min thumb grabbable when both sit at the top end.
        minThumb.style.zIndex = Number(minThumb.value) >= hi ? '5' : '3';
        maxThumb.style.zIndex = '4';
      };

      const store = (a, b) => {
        range.min = a <= lo ? '' : String(a);
        range.max = b >= hi ? '' : String(b);
      };

      const commit = this.debounce(async () => {
        const a = round(Math.min(Number(minThumb.value), Number(maxThumb.value)));
        const b = round(Math.max(Number(minThumb.value), Number(maxThumb.value)));
        store(a, b);
        await this.refetchAndRender();
      }, 450);

      const onDrag = () => {
        if (minInput) minInput.value = round(Math.min(Number(minThumb.value), Number(maxThumb.value)));
        if (maxInput) maxInput.value = round(Math.max(Number(minThumb.value), Number(maxThumb.value)));
        paint();
      };
      minThumb.addEventListener('input', onDrag);
      maxThumb.addEventListener('input', onDrag);
      minThumb.addEventListener('change', commit);
      maxThumb.addEventListener('change', commit);

      const onBox = this.debounce(async () => {
        let a = parseFloat(String(minInput.value).replace(/[^0-9.]/g, ''));
        let b = parseFloat(String(maxInput.value).replace(/[^0-9.]/g, ''));
        if (isNaN(a)) a = lo;
        if (isNaN(b)) b = hi;
        a = round(Math.min(Math.max(a, lo), hi));
        b = round(Math.min(Math.max(b, lo), hi));
        if (a > b) { const t = a; a = b; b = t; }
        minThumb.value = a; maxThumb.value = b;
        minInput.value = a; maxInput.value = b;
        paint();
        store(a, b);
        await this.refetchAndRender();
      }, 600);
      if (minInput) minInput.addEventListener('change', onBox);
      if (maxInput) maxInput.addEventListener('change', onBox);

      paint();
    }

    facetListHtml(def) {
      if (!def.values?.length) return '';
      const description = this.descriptionFor(def);
      const items = def.values.map(v => {
        const key = `${def.id}::${v.id}`;
        const checked = this.activeFilters.has(key) ? 'checked' : '';
        return `
          <label class="group flex items-center gap-1.5 py-1 cursor-pointer text-sm">
            <input type="checkbox" class="shrink-0" data-facet-key="${this.escapeHtml(key)}" data-facet-id="${this.escapeHtml(def.id)}" data-facet-label="${this.escapeHtml(v.label)}" data-facet-input='${this.escapeAttr(v.input)}' ${checked} />
            <span class="group-hover:underline">${this.escapeHtml(v.label)}</span>
            <span class="text-primary-300 text-xs ml-auto">(${v.count})</span>
          </label>
        `;
      }).join('');
      return `
        <details class="facet border-b border-base-border pb-4" data-facet-key="${this.escapeHtml(def.id)}" open>
          <summary class="flex items-center justify-between py-2 cursor-pointer select-none">
            <span class="uppercase text-sm">${this.escapeHtml(def.label)}</span>
            <span class="facet-toggle inline-flex items-center justify-center size-4 transition-transform">${CHEVRON_SVG}</span>
          </summary>
          ${description ? `<p class="text-sm text-primary-400 mt-2 mb-2">${this.escapeHtml(description)}</p>` : ''}
          <div class="flex flex-col mt-1 max-h-[260px] overflow-y-auto pr-3">${items}</div>
        </details>
      `;
    }

    bindFacetEvents(scope) {
      // Dual-handle range sliders (price, carat)
      this.bindRangeSliders(scope);

      // List checkboxes
      scope.querySelectorAll('input[type="checkbox"][data-facet-key]').forEach(cb => {
        cb.addEventListener('change', async () => {
          const key = cb.dataset.facetKey;
          if (cb.checked) {
            this.activeFilters.set(key, {
              key,
              filterId: cb.dataset.facetId,
              label: cb.dataset.facetLabel,
              input: cb.dataset.facetInput,
            });
          } else {
            this.activeFilters.delete(key);
          }
          await this.refetchAndRender();
        });
      });

      // Rotate chevron on details open/close
      scope.querySelectorAll('details.facet').forEach(d => {
        const toggle = d.querySelector('.facet-toggle');
        if (!toggle) return;
        const apply = () => {
          toggle.style.transform = d.open ? 'rotate(180deg)' : '';
        };
        apply();
        d.addEventListener('toggle', apply);
      });
    }

    async refetchAndRender() {
      this.showLoading();
      this.writeUrlState();
      await this.fetchAll();
      this.renderFacets();
      this.applyAndRender();
      this.hideLoading();
    }

    renderActiveFacets() {
      const container = this.querySelector('[data-active-facets]');
      if (!container) return;
      const chips = [];
      this.activeFilters.forEach(entry => {
        chips.push(`
          <button type="button" data-remove-facet="${this.escapeHtml(entry.key)}" class="inline-flex items-center gap-1 text-xs border border-base-border px-2 py-1">
            <span>${this.escapeHtml(entry.label)}</span>
            <span aria-hidden="true">✕</span>
          </button>
        `);
      });
      if (this.priceRange.min || this.priceRange.max) {
        chips.push(`
          <button type="button" data-remove-price class="inline-flex items-center gap-1 text-xs border border-base-border px-2 py-1">
            <span>Price: ${this.priceRange.min || '0'}–${this.priceRange.max || '∞'}</span>
            <span aria-hidden="true">✕</span>
          </button>
        `);
      }
      if (this.caratRange.min || this.caratRange.max) {
        chips.push(`
          <button type="button" data-remove-carat class="inline-flex items-center gap-1 text-xs border border-base-border px-2 py-1">
            <span>Carat: ${this.caratRange.min || '0'}–${this.caratRange.max || '∞'} ct</span>
            <span aria-hidden="true">✕</span>
          </button>
        `);
      }
      if (chips.length) {
        chips.push(`<button type="button" data-reset-all class="text-xs underline ml-2">Clear all</button>`);
      }
      container.innerHTML = chips.join('');
      // Collapse the row entirely when no filters are applied so the toolbar
      // row-gap doesn't leave empty space above the sort/view bar.
      container.style.display = chips.length ? '' : 'none';

      container.querySelectorAll('[data-remove-facet]').forEach(btn => {
        btn.addEventListener('click', async () => {
          this.activeFilters.delete(btn.dataset.removeFacet);
          await this.refetchAndRender();
        });
      });
      const priceBtn = container.querySelector('[data-remove-price]');
      if (priceBtn) priceBtn.addEventListener('click', async () => {
        this.priceRange = { min: '', max: '' };
        await this.refetchAndRender();
      });
      const caratBtn = container.querySelector('[data-remove-carat]');
      if (caratBtn) caratBtn.addEventListener('click', async () => {
        this.caratRange = { min: '', max: '' };
        await this.refetchAndRender();
      });
      const resetBtn = container.querySelector('[data-reset-all]');
      if (resetBtn) resetBtn.addEventListener('click', async () => {
        this.activeFilters.clear();
        this.priceRange = { min: '', max: '' };
        this.caratRange = { min: '', max: '' };
        await this.refetchAndRender();
      });
    }

    /* ---------------- Items render ---------------- */

    applyAndRender() {
      const sort = SORT_OPTIONS.find(o => o.id === this.sortId) || SORT_OPTIONS[0];
      let items = this.allItems.slice();
      // Server already sorted by price; client-sort for carat
      if (sort.sortField === 'carat') {
        items.sort((a, b) => {
          const ac = parseFloat(a.stone_carat?.value || '0');
          const bc = parseFloat(b.stone_carat?.value || '0');
          return sort.reverse ? bc - ac : ac - bc;
        });
      }
      this.sortedItems = items;
      this.renderCount(items.length);
      this.renderItems();
    }

    renderCount(count) {
      const el = this.querySelector('[data-items-count]');
      if (!el) return;
      // Count is hidden on mobile (sort sits left, view right); shown on laptop+.
      // Hidden everywhere when results hit the 500 fetch cap (count is incomplete).
      if (this.truncated) {
        el.textContent = '';
        el.className = 'hidden text-sm whitespace-nowrap';
        return;
      }
      el.className = 'hidden text-sm whitespace-nowrap laptop:block';
      const noun = count === 1 ? this.stoneType : `${this.stoneType}s`;
      el.textContent = `${count.toLocaleString()} ${noun}`;
    }

    renderItems() {
      const container = this.querySelector('[data-items-container]');
      if (!container) return;
      const items = this.sortedItems || [];
      if (!items.length) {
        container.className = '';
        container.innerHTML = `
          <div class="py-10 text-center">
            <p class="mb-3">No ${this.stoneType}s match your filters.</p>
            <button type="button" data-reset-all class="text-sm underline">Clear all filters</button>
          </div>
        `;
        const reset = container.querySelector('[data-reset-all]');
        if (reset) reset.addEventListener('click', async () => {
          this.activeFilters.clear();
          this.priceRange = { min: '', max: '' };
          this.caratRange = { min: '', max: '' };
          await this.refetchAndRender();
        });
        return;
      }

      if (this.viewMode === 'grid') {
        container.className = 'grid grid-cols-2 gap-4 tablet:grid-cols-3 tablet:gap-4 laptop:grid-cols-3 laptop:gap-4';
        container.innerHTML = items.map(p => `<stone-card data-handle="${this.escapeHtml(p.handle)}"></stone-card>`).join('');
      } else {
        container.className = '';
        container.innerHTML = '';
        container.appendChild(this.buildTable(items));
      }
    }

    buildTable(items) {
      const wrapper = document.createElement('div');
      wrapper.className = 'w-full overflow-x-auto';

      const table = document.createElement('table');
      table.className = 'w-full border-collapse min-w-full';

      const thead = document.createElement('thead');
      const headerRow = document.createElement('tr');
      headerRow.className = 'bg-black text-white';

      const sort = SORT_OPTIONS.find(o => o.id === this.sortId);
      const sortField = sort?.sortField;
      const reverse = sort?.reverse;

      const columns = this.stoneType === 'gemstone' ? GEMSTONE_TABLE_COLUMNS : TABLE_COLUMNS;

      columns.forEach(col => {
        const th = document.createElement('th');
        th.className = 'px-4 py-3 text-left text-sm font-medium whitespace-nowrap';

        if (col.sortable) {
          th.className += ' cursor-pointer hover:bg-gray-800 select-none';
          th.setAttribute('role', 'button');
          th.setAttribute('tabindex', '0');

          const inner = document.createElement('div');
          inner.className = 'flex items-center justify-between gap-2';

          const lbl = document.createElement('span');
          lbl.textContent = col.label;
          inner.appendChild(lbl);

          const arrowWrap = document.createElement('span');
          arrowWrap.className = 'flex items-center justify-center';
          arrowWrap.style.width = '24px';
          arrowWrap.style.height = '24px';
          arrowWrap.style.flexShrink = '0';

          const isCurrent = sortField === col.sortField;
          const isAsc = isCurrent && !reverse;

          arrowWrap.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
              style="${isAsc ? 'transform: rotate(180deg);' : ''} opacity:${isCurrent ? 1 : 0.3};">
              <path d="M7 10L12 15L17 10H7Z" fill="white"/>
            </svg>
          `;
          inner.appendChild(arrowWrap);
          th.appendChild(inner);

          const onSort = () => this.toggleSortField(col.sortField);
          th.addEventListener('click', (e) => { e.preventDefault(); onSort(); });
          th.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(); }
          });
        } else {
          th.textContent = col.label;
        }

        headerRow.appendChild(th);
      });

      thead.appendChild(headerRow);
      table.appendChild(thead);

      const tbody = document.createElement('tbody');
      items.forEach((item, idx) => {
        const tr = document.createElement('tr');
        tr.className = 'transition-colors cursor-pointer';
        tr.style.backgroundColor = idx % 2 === 1 ? '#F8F8F8' : 'white';
        tr.addEventListener('mouseenter', () => { tr.style.backgroundColor = '#e5e5e5'; });
        tr.addEventListener('mouseleave', () => {
          tr.style.backgroundColor = idx % 2 === 1 ? '#F8F8F8' : 'white';
        });
        tr.addEventListener('click', (e) => {
          if (e.target.closest('a')) return;
          window.location.href = `/products/${item.handle}`;
        });

        columns.forEach(col => {
          const td = document.createElement('td');
          td.className = 'px-4 py-3 whitespace-nowrap';
          td.innerHTML = this.renderCell(col.key, item);
          tr.appendChild(td);
        });

        tbody.appendChild(tr);
      });
      table.appendChild(tbody);
      wrapper.appendChild(table);
      return wrapper;
    }

    renderCell(key, item) {
      const shape = item.stone_shape?.value || '';
      const carat = item.stone_carat?.value || '';
      const cut = item.stone_cut?.value || '';
      const grade = item.stone_grade?.value || '';
      const origin = item.stone_origin?.value || '';
      const priceAmt = parseFloat(item.priceRange?.minVariantPrice?.amount || '0');
      const priceCents = Math.round(priceAmt * 100);
      const href = `/products/${item.handle}`;

      switch (key) {
        case 'shape': {
          const img = item.featuredImage?.url
            ? `<img src="${this.sizedUrl(item.featuredImage.url, 60)}" alt="${this.escapeHtml(shape)}" width="30" height="30" class="w-[30px] h-[30px] object-cover block shrink-0" loading="lazy" />`
            : '';
          return `
            <a href="${href}" class="flex items-center gap-3 group hover:no-underline">
              ${img}
              <span class="text-sm group-hover:underline">${this.escapeHtml(shape || item.title)}</span>
            </a>
          `;
        }
        case 'carat': return `<span class="text-sm">${this.escapeHtml(carat)}</span>`;
        case 'price': return `<span class="text-sm font-medium text-primary-500">${window.theme.money.format(priceCents, true)}</span>`;
        case 'cut':   return `<span class="text-sm">${this.escapeHtml(cut || 'N/A')}</span>`;
        case 'grade': return `<span class="text-sm">${this.escapeHtml(grade || 'N/A')}</span>`;
        case 'origin':return `<span class="text-sm">${this.escapeHtml(origin)}</span>`;
      }
      return '';
    }

    toggleSortField(field) {
      const current = SORT_OPTIONS.find(o => o.id === this.sortId);
      let nextId;
      if (current?.sortField === field) {
        // toggle reverse
        nextId = SORT_OPTIONS.find(o => o.sortField === field && o.reverse !== current.reverse)?.id;
      } else {
        nextId = SORT_OPTIONS.find(o => o.sortField === field && !o.reverse)?.id;
      }
      if (!nextId) return;
      this.sortId = nextId;
      const sel = this.querySelector('[data-sort]');
      if (sel) sel.value = nextId;
      this.writeUrlState();
      // Price requires server resort; carat client-side only
      const target = SORT_OPTIONS.find(o => o.id === nextId);
      if (target?.sortKey === 'PRICE') {
        this.showLoading();
        this.fetchAll().then(() => { this.renderFacets(); this.applyAndRender(); this.hideLoading(); });
      } else {
        this.applyAndRender();
      }
    }

    /* ---------------- helpers ---------------- */

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    escapeHtml(str) {
      if (str == null) return '';
      return String(str)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    }

    escapeAttr(str) {
      if (str == null) return '';
      return String(str).replaceAll("'", '&#39;').replaceAll('"', '&quot;');
    }

    sizedUrl(url, width) {
      if (!url) return '';
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('width', String(width));
        return u.toString();
      } catch (_) { return url; }
    }
  }

  customElements.define('shopify-stone-filters', ShopifyStoneFilters);
}
